import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { fetchAndStoreParticipants } from 'hooks/useParticipantsBackfill';
import { CallScribeParticipantsForCallSelector } from 'store/callScribe/selectors';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import { muteParticipant, removeParticipant } from 'services/callScribe/conference';
import {
  PARTICIPANT_STATUS_TEXT,
  SPEAKER_LABEL,
  isActiveStatus,
  isNamed,
  isOwnTechnician,
  normaliseParticipantStatus,
  normaliseSpeaker,
} from 'services/callScribe/participants';
import { CallScribeParticipantStatus, ICallScribeParticipant } from 'services/callScribe/types';
import { isNoAutomatedDisclosure, useCallMeta } from 'components/CallScribe/Conference/useCallMeta';
import usePeopleStripStyles from 'components/CallScribe/Conference/peopleStripStyles';
import Avatar from 'components/CallScribe/ui/Avatar';
import { slotForPerson, speakerSlots } from 'components/CallScribe/speakerSlots';

export const MIN_PEOPLE_FOR_STRIP = 2;

const labelOf = (p: ICallScribeParticipant): string =>
  p.label?.trim() || SPEAKER_LABEL[normaliseSpeaker(p.role)];

const shortNameOf = (p: ICallScribeParticipant): string => {
  const label = labelOf(p);
  if (!isNamed(p) || !/\p{L}/u.test(label)) return label;
  return label.split(/\s+/)[0] ?? label;
};

const metaOf = (p: ICallScribeParticipant, status: CallScribeParticipantStatus): string =>
  `${SPEAKER_LABEL[normaliseSpeaker(p.role)]} · ${PARTICIPANT_STATUS_TEXT[status]}${p.isMuted ? ' · Muted' : ''}`;

const spokenMetaOf = (p: ICallScribeParticipant, status: CallScribeParticipantStatus): string =>
  `${SPEAKER_LABEL[normaliseSpeaker(p.role)]}, ${PARTICIPANT_STATUS_TEXT[status]}${p.isMuted ? ', muted' : ''}`;

const stateOf = (
  p: ICallScribeParticipant,
  status: CallScribeParticipantStatus,
  own: boolean,
): string => {
  if (own) return 'You';
  if (status === CallScribeParticipantStatus.Joined) {
    return p.isMuted ? 'Muted' : SPEAKER_LABEL[normaliseSpeaker(p.role)];
  }
  return PARTICIPANT_STATUS_TEXT[status];
};

export const joinAnnouncements = (
  previous: ReadonlyMap<number, CallScribeParticipantStatus>,
  participants: ICallScribeParticipant[],
  hydrated = false,
): { joined: ICallScribeParticipant[]; text: string } => {
  const joined: ICallScribeParticipant[] = [];
  const lines: string[] = [];
  participants.forEach(p => {
    const before = previous.get(p.id);
    if (isOwnTechnician(p)) return;
    const now = normaliseParticipantStatus(p.status);
    if (before === undefined) {
      if (hydrated && now === CallScribeParticipantStatus.Joined) {
        joined.push(p);
        lines.push(`${labelOf(p)} joined`);
      }
      return;
    }
    if (before !== CallScribeParticipantStatus.Joined && now === CallScribeParticipantStatus.Joined) {
      joined.push(p);
      lines.push(`${labelOf(p)} joined`);
    } else if (before === CallScribeParticipantStatus.Joined && now === CallScribeParticipantStatus.Left) {
      lines.push(`${labelOf(p)} left`);
    }
  });
  return { joined, text: lines.join('. ') };
};

export interface IPeopleStripProps {
  callId: number | undefined;
  onAdd?: () => void;
  addDisabled?: boolean;
}

const PeopleStrip: React.FC<IPeopleStripProps> = ({ callId, onAdd, addDisabled = false }) => {
  const classes = usePeopleStripStyles();
  const dispatch = useAppDispatch();
  const participants = useAppSelector(CallScribeParticipantsForCallSelector(callId));
  const list = participants ?? [];
  const visible = typeof callId === 'number' && list.length >= MIN_PEOPLE_FOR_STRIP;
  const meta = useCallMeta(callId, visible);

  const previous = useRef<Map<number, CallScribeParticipantStatus>>(new Map());
  const hydrated = useRef(false);
  const [announcement, setAnnouncement] = useState('');
  const [remind, setRemind] = useState<ICallScribeParticipant[]>([]);
  const [confirming, setConfirming] = useState<ICallScribeParticipant | undefined>(undefined);
  const [busyId, setBusyId] = useState<number | undefined>(undefined);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<number | undefined>(undefined);
  const focusTrayRef = useRef(false);
  const trayRef = useRef<HTMLDivElement | null>(null);
  const personRefs = useRef(new Map<number, HTMLButtonElement>());
  const trayId = useId();
  const slots = speakerSlots(list);
  const open = list.find(p => p.id === openId);

  useEffect(() => {
    if (openId !== undefined && !open) setOpenId(undefined);
  }, [openId, open]);

  useEffect(() => {
    if (!focusTrayRef.current || !trayRef.current) return;
    focusTrayRef.current = false;
    const first = trayRef.current.querySelector<HTMLElement>('button:not(:disabled)');
    (first ?? trayRef.current).focus();
  }, [openId]);

  const toggle = (p: ICallScribeParticipant, fromKeyboard: boolean): void => {
    if (openId === p.id) {
      setOpenId(undefined);
      return;
    }
    focusTrayRef.current = fromKeyboard;
    setOpenId(p.id);
  };

  const closeTray = (): void => {
    const id = openId;
    setOpenId(undefined);
    if (typeof id === 'number') personRefs.current.get(id)?.focus();
  };

  useEffect(() => {
    const { joined, text } = joinAnnouncements(previous.current, list, hydrated.current);
    if (text) setAnnouncement(text);
    const added = joined.filter(p => !p.isPrimary);
    if (added.length > 0) {
      setRemind(current => [
        ...current,
        ...added.filter(p => !current.some(c => c.id === p.id)),
      ]);
    }
    previous.current = new Map(list.map(p => [p.id, normaliseParticipantStatus(p.status)]));
    if (participants !== undefined) hydrated.current = true;
  }, [participants]);

  const refresh = (): void => {
    if (typeof callId === 'number') void fetchAndStoreParticipants(dispatch as never, callId);
  };

  const toggleMute = (p: ICallScribeParticipant): void => {
    if (typeof callId !== 'number' || busyId !== undefined) return;
    setBusyId(p.id);
    setError('');
    void muteParticipant(callId, p.id, !p.isMuted)
      .then(refresh)
      .catch((err: unknown) => {
        setError(extractApiErrorMessage(err, `Couldn't change the mute for ${labelOf(p)}.`));
      })
      .finally(() => { setBusyId(undefined); });
  };

  const confirmRemove = (): void => {
    const target = confirming;
    if (!target || typeof callId !== 'number') return;
    setConfirming(undefined);
    setBusyId(target.id);
    setError('');
    void removeParticipant(callId, target.id)
      .then(refresh)
      .catch((err: unknown) => {
        setError(extractApiErrorMessage(err, `Couldn't remove ${labelOf(target)}.`));
      })
      .finally(() => { setBusyId(undefined); });
  };

  const showReminders = meta?.consentMode == null || isNoAutomatedDisclosure(meta.consentMode);

  return (
    <>
      <div className={classes.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {visible ? (
        <section className={classes.root} aria-label="People on this call">
          <ul className={classes.row} data-people-scroll>
            {list.map(p => {
              const status = normaliseParticipantStatus(p.status);
              const own = isOwnTechnician(p);
              const label = labelOf(p);
              const ringing = status === CallScribeParticipantStatus.Ringing;
              const gone = !isActiveStatus(status);
              const face = (
                <span
                  className={[
                    classes.face,
                    ringing ? classes.faceRinging : '',
                    openId === p.id ? classes.faceOpen : '',
                  ].filter(Boolean).join(' ')}
                >
                  <Avatar
                    name={label}
                    tone={gone ? null : slotForPerson(p, slots)}
                    surface="stage"
                    size="lg"
                    icon={<PhoneRoundedIcon />}
                  />
                  {p.isMuted && status === CallScribeParticipantStatus.Joined ? (
                    <span className={classes.mutedBadge} aria-hidden>
                      <MicOffRoundedIcon fontSize="inherit" />
                    </span>
                  ) : null}
                </span>
              );
              const words = (
                <>
                  <span className={classes.shortName}>{shortNameOf(p)}</span>
                  <span className={classes.state}>{stateOf(p, status, own)}</span>
                </>
              );
              return (
                <li key={p.id} className={classes.item}>
                  {own ? (
                    <span className={classes.person}>{face}{words}</span>
                  ) : (
                    <button
                      type="button"
                      ref={el => {
                        if (el) personRefs.current.set(p.id, el);
                        else personRefs.current.delete(p.id);
                      }}
                      className={`${classes.person} ${classes.personButton}`}
                      aria-label={`${label}, ${spokenMetaOf(p, status)}`}
                      title={label}
                      aria-expanded={openId === p.id}
                      aria-controls={openId === p.id ? trayId : undefined}
                      onClick={e => { toggle(p, e.detail === 0); }}
                      onKeyDown={e => {
                        if (e.key === 'Escape' && openId === p.id) {
                          e.stopPropagation();
                          setOpenId(undefined);
                        }
                      }}
                    >
                      {face}
                      {words}
                    </button>
                  )}
                </li>
              );
            })}
            {onAdd ? (
              <li className={classes.item}>
                <button
                  type="button"
                  className={`${classes.person} ${classes.personButton} ${classes.add}`}
                  aria-label="Add a person to this call"
                  aria-haspopup="dialog"
                  disabled={addDisabled}
                  onClick={onAdd}
                >
                  <span className={classes.addFace} aria-hidden>
                    <PersonAddAltRoundedIcon fontSize="inherit" />
                  </span>
                  <span className={classes.shortName}>Add</span>
                </button>
              </li>
            ) : null}
          </ul>
          {open ? (() => {
            const status = normaliseParticipantStatus(open.status);
            const label = labelOf(open);
            const own = isOwnTechnician(open);
            return (
              <div
                id={trayId}
                ref={trayRef}
                role="group"
                aria-label={label}
                tabIndex={-1}
                className={classes.tray}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    closeTray();
                  }
                }}
              >
                <span className={classes.trayWho}>
                  <span className={classes.trayName}>{label}</span>
                  <span className={classes.trayMeta}>{metaOf(open, status)}</span>
                </span>
                {own ? null : (
                  <span className={classes.trayActions}>
                    {status === CallScribeParticipantStatus.Joined ? (
                      <button
                        type="button"
                        className={classes.trayButton}
                        aria-label={`${open.isMuted ? 'Unmute' : 'Mute'} ${label}`}
                        disabled={busyId === open.id}
                        onClick={() => { toggleMute(open); }}
                      >
                        {open.isMuted ? <MicRoundedIcon aria-hidden /> : <MicOffRoundedIcon aria-hidden />}
                        {open.isMuted ? 'Unmute' : 'Mute'}
                      </button>
                    ) : null}
                    {isActiveStatus(status) ? (
                      <button
                        type="button"
                        className={`${classes.trayButton} ${classes.trayRemove}`}
                        aria-label={`Remove ${label}`}
                        disabled={busyId === open.id}
                        onClick={() => { setConfirming(open); }}
                      >
                        <CloseRoundedIcon aria-hidden />
                        Remove
                      </button>
                    ) : null}
                  </span>
                )}
              </div>
            );
          })() : null}
          {showReminders
            ? remind.map(p => (
              <div key={p.id} className={classes.banner} role="status">
                <span className={classes.bannerText}>
                  {`Tell ${labelOf(p)} this call is recorded and transcribed.`}
                </span>
                <button
                  type="button"
                  className={classes.button}
                  aria-label={`Dismiss the reminder for ${labelOf(p)}`}
                  onClick={() => { setRemind(c => c.filter(x => x.id !== p.id)); }}
                >
                  Dismiss
                </button>
              </div>
            ))
            : null}
          {error ? <p className={classes.error} role="alert">{error}</p> : null}
        </section>
      ) : null}
      <Dialog
        open={confirming !== undefined}
        onClose={() => { setConfirming(undefined); }}
        aria-labelledby="remove-person-title"
      >
        <DialogTitle id="remove-person-title">
          {confirming ? `Remove ${labelOf(confirming)} from the call?` : ''}
        </DialogTitle>
        <DialogContent>They will be disconnected from this call.</DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirming(undefined); }}>Cancel</Button>
          <Button variant="contained" onClick={confirmRemove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PeopleStrip;
